import { useMap } from '@vis.gl/react-google-maps'
import { ReactElement } from 'react'
import styled from 'styled-components'

import { getOptimizationByDow } from 'features/RoutingProjects/models'
import { IRoutingProjectPlanModel } from 'features/RoutingProjects/types'
import { adjustMapBoundsByRoutes } from 'features/RoutingProjects/utils'
import { RoutingProjectDow } from 'gql/generated/graphql'

const MAX_ROUTES_TO_DISPLAY = 9

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
`

const TabButton = styled.button<{ isSelected: boolean; isFirst?: boolean; isLast?: boolean }>`
  padding: 8px 6px;
  border: 1px solid ${({ isSelected }): string => (isSelected ? '#5D59D7' : '#e3e3e3')};
  border-right: none; /* Remove right border as it will be set conditionally per tab */
  background: ${({ isSelected }): string => (isSelected ? '#8F8BFF' : 'none')};
  cursor: pointer;
  font-size: 14px;
  border-radius: 0;
  font-weight: 400;
  transition: background-color 0.2s ease;
  color: ${({ isSelected }): string => (isSelected ? '#fff' : '#28265D')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  margin: 0;
  height: 16px;
  width: 46px;
  box-sizing: content-box;

  /* Only add right border to the selected tab or the last tab */
  ${({ isSelected, isLast }): string =>
    isSelected || isLast ? `border-right: 1px solid ${isSelected ? '#5D59D7' : '#e3e3e3'};` : ''}

  ${({ isFirst }): string =>
    isFirst
      ? `
    border-top-left-radius: 24px;
    border-bottom-left-radius: 24px;
  `
      : ''}

  ${({ isLast }): string =>
    isLast
      ? `
    border-top-right-radius: 24px;
    border-bottom-right-radius: 24px;
  `
      : ''}

  &:hover {
    background-color: ${({ isSelected }): string => (isSelected ? '#8F8BFF' : '#EAEAFF')};
  }
`

const RoutesCountBadge = styled.span<{ isSelected: boolean; isShowingPlusIndicator: boolean }>`
  position: relative;
  background-color: ${({ isSelected }): string => (isSelected ? '#EAEAFF' : '#8F8BFF')};
  color: ${({ isSelected }): string => (isSelected ? '#28265D' : '#fff')};
  border-radius: 32px;
  font-size: ${({ isShowingPlusIndicator }): string => (isShowingPlusIndicator ? '8px' : '10px')};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: ${({ isShowingPlusIndicator }): string => (isShowingPlusIndicator ? '13px' : '16px')};
`

const OrphansBadge = styled.span<{ isTabSelected: boolean }>`
  position: absolute;
  width: 5px;
  height: 5px;
  top: -2px;
  right: -2px;
  background-color: #ff627b;
  border: 1px solid ${({ isTabSelected }): string => (isTabSelected ? '#8F8BFF' : '#fff')};
  border-radius: 50%;
`

const PlusIndicator = styled.span<{ isSelected: boolean }>`
  position: relative;
  font-size: 8px;
  line-height: 8px;
  right: -1px;
  top: 3px;
  color: ${({ isSelected }): string => (isSelected ? '#28265D' : '#fff')};
  font-weight: 600;
`

interface ISelectDowProps {
  selectedDow: RoutingProjectDow
  setSelectedDow: (dow: RoutingProjectDow) => void
  planModel: IRoutingProjectPlanModel
}

export const SelectDow = ({ selectedDow, setSelectedDow, planModel }: ISelectDowProps): ReactElement => {
  const map = useMap()

  const handleChange = (dow: RoutingProjectDow): void => {
    setSelectedDow(dow)
    if (map) {
      const currentOptimization = getOptimizationByDow(planModel.optimizations, dow)
      const updatedRoutesIndex = currentOptimization?.routes.map((route, index) => index) || []
      setTimeout(() => {
        adjustMapBoundsByRoutes({
          map,
          routes: currentOptimization?.routes.filter((_, index) => updatedRoutesIndex.includes(index)) || []
        })
      }, 1)
    }
  }

  const daysOfWeek = [
    { label: 'Mon', value: RoutingProjectDow.Monday },
    { label: 'Tue', value: RoutingProjectDow.Tuesday },
    { label: 'Wed', value: RoutingProjectDow.Wednesday },
    { label: 'Thu', value: RoutingProjectDow.Thursday },
    { label: 'Fri', value: RoutingProjectDow.Friday },
    { label: 'Sat', value: RoutingProjectDow.Saturday },
    { label: 'Sun', value: RoutingProjectDow.Sunday }
  ].filter(({ value }) => planModel.optimizations.some((opt) => opt.dow === value))

  return (
    <TabsContainer>
      {daysOfWeek.map(({ label, value }, index) => {
        const isFirst = index === 0
        const isLast = index === daysOfWeek.length - 1
        const optimization = getOptimizationByDow(planModel.optimizations, value)

        if (!optimization) return null

        const hasOrphans = optimization.skippedLocations.length > 0
        const isTabSelected = selectedDow === value
        const isShowingPlusIndicator = optimization.routes.length > MAX_ROUTES_TO_DISPLAY

        return (
          <TabButton
            key={value}
            isSelected={isTabSelected}
            onClick={(): void => handleChange(value)}
            isFirst={isFirst}
            isLast={isLast}
          >
            {label}
            <RoutesCountBadge isSelected={isTabSelected} isShowingPlusIndicator={isShowingPlusIndicator}>
              {optimization.routes.length <= MAX_ROUTES_TO_DISPLAY ? optimization.routes.length : MAX_ROUTES_TO_DISPLAY}
              {isShowingPlusIndicator && <PlusIndicator isSelected={isTabSelected}>+</PlusIndicator>}
              {hasOrphans && <OrphansBadge isTabSelected={isTabSelected} />}
            </RoutesCountBadge>
          </TabButton>
        )
      })}
    </TabsContainer>
  )
}
